import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import SkeletonForm from '../../../../../components/Form/Skeleton'
import WarningMessage from '../../../../../features/components/Form/WarningMessage'
import UploadAdCreativeCampaignForm from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm'
import InternalLink from '../../../../../components/InternalLink'
import NewCampaignFields from './NewCampaignFields'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import useRightBudgetAndImpressionsCalculation from '../../../../Multiplatform/UploadAdCreative/useRightBudgetAndImpressionsCalculation'
import { useHasMissingClientApps, useHasMissingDatasetAndCustomConversion } from './NewCampaignFields/hooks'

import { formatOptionsList } from '../../../../../features/formatters'

import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { getLineItems } from '../../../../../modules/actions/lineItems'
import { createCampaign } from '../../../../../modules/actions/campaigns'
import { getAccessibleSocialPages } from '../../../../../modules/actions/socialAccounts'
import { combinedCampaignsSelector } from '../../../../../modules/selectors/combinedData'
import { campaignCreateIsLoadingSelector } from '../../../../../modules/selectors/campaigns'
import { lineItemsLoadingSelector } from '../../../../../modules/selectors/lineItems'
import { selectedFacebookAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import { rightSelector } from '../../../../../modules/selectors/rights'
import {
  accessibleSocialPagesIsLoadingSelector,
  accessibleSocialPagesSelector,
  accessibleSocialPagesWasLoadedSelector
} from '../../../../../modules/selectors/socialAccounts'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'

import { CAMPAIGN } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'
import { FACEBOOK_PLATFORM, FACEBOOK_PROVIDER_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { LIFETIME_DATE_PRESET } from '../../../../../constants/selectLists/listItemFiltersList'
import { DIGITAL_ALLOCATION_TYPE_IMPRESSIONS } from '../../../../Multiplatform/Partnerships/RightForms/fields'
import { ROUTES } from '../../../../../constants/routes'
import {
  BUDGET_LIFETIME,
  getInitialValues
} from '../../../../Multiplatform/CombinedFacebookTiktokCreate/CreateFormSelector/CampaignCreateForm/fields'

import { OBJECTIVE } from '../../../../Multiplatform/CampaignForms/fields'

import { BIDDING_TYPE } from '../../../CampaignForms/CampaignFacebookCreate/CampaignFacebookCreateForm/fields'
import { IMPRESSIONS } from '../../../../Google/UploadGoogleAdCreative/UploadGoogleAdCreativeForm/CampaignForm/fields'
import { PACING_TYPE } from '../../../../DV360/UploadDV360AdCreative/UploadDV360AdCreativeForm/CampaignForm/fields'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import { facebookClientAppsSelector } from '../../../../../modules/selectors/choices'
import { APPLICATION_ID } from '../reusableFields/AppListField/fields'
import { getValidationSchema } from '../../../../Multiplatform/CombinedFacebookTiktokCreate/CreateFormSelector/CampaignCreateForm/validation'
import { createCampaignPayload } from '../../../../Multiplatform/CombinedFacebookTiktokCreate/CreateFormSelector/CampaignCreateForm/formatters'

const campaignStepValidationFields = [
  BUDGET_LIFETIME,
  BIDDING_TYPE,
  OBJECTIVE,
  IMPRESSIONS,
  PACING_TYPE,
  APPLICATION_ID
]

const CampaignForm = ({ formProgressItems, onSuccessSubmit }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const facebookPages = useSelector(accessibleSocialPagesSelector)
  const facebookPagesIsLoading = useSelector(accessibleSocialPagesIsLoadingSelector)
  const facebookPagesWasLoaded = useSelector(accessibleSocialPagesWasLoadedSelector)
  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const facebookClientApps = useSelector(facebookClientAppsSelector)

  const combinedCampaigns = useSelector(combinedCampaignsSelector)
  const right = useSelector(rightSelector)

  const { allocation_type: rightAllocationType } = right

  const isImpressionAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const { id: facebookAdAccountId, currency_symbol: currencySymbol } = useSelector(selectedFacebookAdAccountSelector)

  const { campaigns = [] } = combinedCampaigns

  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const facebookCampaigns = useMemo(
    () => campaigns.filter(campaign => campaign.provider === FACEBOOK_PROVIDER_PLATFORM),
    [campaigns]
  )

  const isCampaignsExist = !!facebookCampaigns.length

  const formattedCampaigns = formatOptionsList({
    list: facebookCampaigns,
    valueName: 'id',
    labelName: 'name'
  })

  const onSubmit = useCallback(
    values => {
      // if existing campaign is selected, find full data and set to redux state + fetch line items
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedCampaignFullData = formattedCampaigns.find(campaign => values[CAMPAIGN] === campaign.id)

        const campaignObjective = selectedCampaignFullData?.objective
        const campaignAdAccount = selectedCampaignFullData?.['ad_account_id'] || selectedCampaignFullData?.['account']

        dispatch(setRightUploadAdData({ campaign: selectedCampaignFullData }, FACEBOOK_PLATFORM))

        dispatch(
          getLineItems(
            {
              account: campaignAdAccount,
              campaign_id: values[CAMPAIGN],
              ...(campaignObjective && { campaign_objective: campaignObjective }),
              date_preset: LIFETIME_DATE_PRESET
            },
            FACEBOOK_PLATFORM
          )
        )
      } else {
        const campaignData = createCampaignPayload({
          values,
          adAccountId: facebookAdAccountId,
          right,
          selectedPartnership,
          facebookClientApps
        })

        dispatch(
          createCampaign({ campaignData }, FACEBOOK_PLATFORM, {
            pushToCombinedCampaignsList: true
          })
        )
      }
    },
    [dispatch, formattedCampaigns, facebookAdAccountId, right, selectedPartnership, facebookClientApps]
  )

  const hasMissingDatasetAndCustomConversion = useHasMissingDatasetAndCustomConversion()
  const hasMissingClientApps = useHasMissingClientApps()

  const formik = useFormik({
    initialValues: getInitialValues({ isCampaignsExist, rightAllocationType }),
    validationSchema: getValidationSchema({
      remainingBudget,
      remainingImpressions,
      currencySymbol,
      allocationTypeImpressions: isImpressionAllocationType,
      hasMissingDatasetAndCustomConversion,
      hasMissingClientApps
    }),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  const { [LIST_ITEM_OPTION]: campaignOption } = values

  // when we use existing campaign, we need to pre-load line items for the next LineItem form
  // in this case submit form loads line item. However, we don't do the same for error selectors,
  // because we don't block user for accessing next form when line items get request failed
  const isLoadingSelector =
    campaignOption === OPTION_EXISTING_LIST_ITEM ? lineItemsLoadingSelector : campaignCreateIsLoadingSelector

  const onSuccessCreateCampaignHandler = useCallback(() => {
    const selectedCampaign = facebookCampaigns.find(campaign => campaign.id === values[CAMPAIGN])
    const selectedCampaignObjective = selectedCampaign?.objective

    if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
      onSuccessSubmit && onSuccessSubmit(selectedCampaignObjective)
    } else {
      onSuccessSubmit && onSuccessSubmit(values[OBJECTIVE])
    }
  }, [facebookCampaigns, values, onSuccessSubmit])

  useEffect(() => {
    // it is better to keep request for AccessibleSocialPages after all checks on the higher level pass:
    // right dates, social auth checker and etc..
    if (!facebookPagesWasLoaded && !facebookPagesIsLoading) {
      dispatch(
        getAccessibleSocialPages({
          platform: FACEBOOK_PROVIDER_PLATFORM
        })
      )
    }
  }, [dispatch, facebookPagesWasLoaded, facebookPagesIsLoading])

  if (!facebookPagesWasLoaded) {
    return <SkeletonForm />
  } else if (facebookPagesWasLoaded && !facebookPages.length) {
    return (
      <WarningMessage
        title="Sorry, you can't upload a Facebook ad yet"
        description={
          <>
            {t('Please approve Facebook page access before creating a Facebook / Instagram ad. You can do it using')}{' '}
            <InternalLink to={ROUTES.pagesAndIdentities}>{t('Settings page')}</InternalLink>.
          </>
        }
      />
    )
  }

  return (
    <UploadAdCreativeCampaignForm
      adAccountId={facebookAdAccountId}
      formik={formik}
      platform={FACEBOOK_PLATFORM}
      onSuccessSubmit={onSuccessCreateCampaignHandler}
      isLoadingSelector={isLoadingSelector}
      existedCampaigns={formattedCampaigns}
      formProgressItems={formProgressItems}
      campaignStepValidationFields={campaignStepValidationFields}
    >
      <NewCampaignFields formik={formik} />
    </UploadAdCreativeCampaignForm>
  )
}

export default CampaignForm
